import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { black, white, transparent, lighterGray } from 'shared/styles/colors';
import { Category, Breakpoint } from 'shared/types';
import * as OptionSets from 'shared/types/optionSets';
import OptionThumbnail from './OptionThumbnail';
import * as LayoutUtils from '../utils/LayoutUtils';
import { media } from "@mverissimoo/emotion-grid";
import { OptionState } from '../types';

interface Props {
  category: string;
  optionState: OptionState;
  updateOption: (selectedOption: number) => void;
}

interface OptionContainerProps {
  isSelected: boolean;
}

const OptionPicker = ({ category, optionState, updateOption }: Props) => {
  const [options, setOptions] = useState<any>([]);
  const [currentOption, setCurrentOption] = useState<number>(1);

  useEffect(() => {
    const refreshOptions = () => {
      switch (category) {
        case Category.HAIR:
          setOptions(OptionSets.HairOptions);
          setCurrentOption(optionState.hair);
          break;
        case Category.EYES:
          setOptions(OptionSets.EyesOptions);
          setCurrentOption(optionState.eyes);
          break;
        case Category.NOSE:
          setOptions(OptionSets.NoseOptions);
          setCurrentOption(optionState.nose);
          break;
        case Category.MOUTH:
          setOptions(OptionSets.MouthOptions);
          setCurrentOption(optionState.mouth);
          break;
        case Category.EARS:
          setOptions(OptionSets.EarsOptions);
          setCurrentOption(optionState.ears);
          break;
        case Category.FACIAL_HAIR:
          setOptions(OptionSets.FacialHairOptions);
          setCurrentOption(optionState.facialHair);
          break;
        case Category.LOGO:
          setOptions(OptionSets.LogoOptions);
          setCurrentOption(optionState.logo);
          break;
        case Category.ACCESSORIES_EYEWEAR: {
          setOptions(OptionSets.AccessoriesEyewearOptions);
          setCurrentOption(optionState.accessories.eyewear);
          break;
        }
        case Category.ACCESSORIES_HEADGEAR: {
          setOptions(OptionSets.AccessoriesHeadgearOptions);
          setCurrentOption(optionState.accessories.headgear);
          break;
        }
        case Category.ACCESSORIES_FANFETTI: {
          setOptions(OptionSets.AccessoriesFanfettiOptions);
          setCurrentOption(optionState.accessories.fanfetti);
          break;
        }
        case (Category.BACKGROUND):
          setOptions(OptionSets.BackgroundOptions);
          setCurrentOption(optionState.background);
          break;
        default:
          setOptions([]);
      }
    }

    refreshOptions();
  }, [category]);

  const changeOption = (color) => {
    setCurrentOption(color);
    updateOption(color);
  };

  return (
    <OuterContainer>
      <OptionGrid>
        {
          options.map((item, i) => {
            return (
              <OptionSquare
                isSelected={item === currentOption}
                onClick={() => changeOption(item)}
              >
                <OptionThumbnail
                  category={category}
                  option={options[i]}
                />
              </OptionSquare>
            );
          })
        }
      </OptionGrid>
    </OuterContainer>
  );
}

export default OptionPicker;

const OuterContainer = styled.div({
  flex: 1,
  height: '100%',
  width: '100%',
});

const OptionGrid = styled.div({
  height: '100%',
  width: '100%',
  display: 'flex',
  flexWrap: 'wrap',
  flexDirection: 'row',
  flexGrow: 1,
  justifyContent: 'flex-start',
  alignContent: 'flex-start',
  backgroundColor: white,
  marginTop: 10,
  [media(Breakpoint.lg)]: {
    marginTop: LayoutUtils.STANDARD_MAIN_UI_SQUARE_MARGIN,
  },
});

const OptionSquare = styled.button<OptionContainerProps>(({ isSelected }) => ({
  height: LayoutUtils.STANDARD_MAIN_UI_SQUARE_SIDE,
  width: LayoutUtils.STANDARD_MAIN_UI_SQUARE_SIDE,
  borderWidth: LayoutUtils.STANDARD_BORDER_WIDTH,
  borderColor: isSelected ? black : lighterGray,
  borderStyle: 'solid',
  borderRadius: 4,
  backgroundColor: transparent,
  padding: 0,
  marginLeft: 8,
  marginBottom: 8,
  [media(Breakpoint.lg)]: {
    marginLeft: LayoutUtils.STANDARD_MAIN_UI_SQUARE_MARGIN,
    marginBottom: LayoutUtils.STANDARD_MAIN_UI_SQUARE_MARGIN,
  },
}));
